import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Container, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import FloatingBlobs from '../../components/FloatingBlobs';

const WA_URL =
  'https://wa.me/918595675155?text=Hi%20BookMyLibrary%20team%2C%20I%20want%20to%20know%20more%20about%20listing%20my%20institute';

const ROTATING_WORDS = ['Grow', 'Scale', 'Launch', 'Transform'];
const ROTATE_INTERVAL_MS = 2400;

const FAKE_LEADS = [
  { initials: 'AR', name: 'Aman R.', detail: 'Interested in JEE 2025 · Lucknow', status: 'NEW', statusColor: 'warning' },
  { initials: 'PS', name: 'Priya S.', detail: 'Interested in NEET 2025 · Kanpur', status: 'CONTACTED', statusColor: 'info' },
  { initials: 'RK', name: 'Rohit K.', detail: 'Interested in UPSC 2025 · Allahabad', status: 'INTERESTED', statusColor: 'success' },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: 'easeOut' },
});

function RotatingWord({ orange, golden }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      component="span"
      sx={{ display: 'inline-block', position: 'relative', minWidth: '3ch' }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={ROTATING_WORDS[index]}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{
            display: 'inline-block',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}
        >
          {ROTATING_WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </Box>
  );
}

RotatingWord.propTypes = {
  orange: PropTypes.string.isRequired,
  golden: PropTypes.string.isRequired,
};

function LeadRow({ lead, delay }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;

  const chipColors = {
    warning: { bg: 'rgba(251,146,60,0.15)', color: '#fb923c' },
    info: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
    success: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
  };
  const chipStyle = chipColors[lead.statusColor] || chipColors.warning;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay, ease: 'easeOut' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.25,
          px: 0.5,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(27,54,93,0.06)'}`,
          '&:last-of-type': { borderBottom: 'none' },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${orange}, ${theme.palette.brand.golden.main})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#fff', fontSize: '0.7rem' }}>
            {lead.initials}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
          >
            {lead.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.68rem',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {lead.detail}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 1,
            py: 0.3,
            borderRadius: '999px',
            background: chipStyle.bg,
            color: chipStyle.color,
            fontSize: '0.6rem',
            fontWeight: 800,
            letterSpacing: '0.06em',
            flexShrink: 0,
          }}
        >
          {lead.status}
        </Box>
      </Box>
    </motion.div>
  );
}

LeadRow.propTypes = {
  lead: PropTypes.shape({
    initials: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    detail: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    statusColor: PropTypes.string.isRequired,
  }).isRequired,
  delay: PropTypes.number,
};

LeadRow.defaultProps = {
  delay: 0,
};

function LeadPipelineMock({ totalCount }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.9)' : '#fff';
  const liveLine =
    totalCount > 0 ? `${totalCount}+ institutes live` : 'Join a growing network';

  return (
    <Box
      sx={{
        background: cardBg,
        borderRadius: '24px',
        p: 3,
        boxShadow: isDark
          ? '0 24px 64px rgba(0,0,0,0.5)'
          : '0 24px 64px rgba(27,54,93,0.18)',
        border: isDark
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(27,54,93,0.08)',
        maxWidth: 380,
        mx: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Your lead pipeline
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '999px',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
          }}
        >
          LIVE
        </Box>
      </Box>

      {/* Lead rows */}
      <Box sx={{ mb: 2 }}>
        {FAKE_LEADS.map((lead, i) => (
          <LeadRow key={lead.name} lead={lead} delay={i * 0.1} />
        ))}
      </Box>

      {/* Live institutes badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderRadius: '12px',
          background: isDark ? 'rgba(27,54,93,0.5)' : 'rgba(27,54,93,0.04)',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#22c55e',
            flexShrink: 0,
            '@keyframes institutePulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%': { opacity: 0.5, transform: 'scale(1.4)' },
            },
            animation: 'institutePulse 2s ease-in-out infinite',
          }}
          aria-hidden="true"
        />
        <Typography variant="caption" color="text.secondary">
          {liveLine}
        </Typography>
      </Box>
    </Box>
  );
}

LeadPipelineMock.propTypes = {
  totalCount: PropTypes.number,
};

LeadPipelineMock.defaultProps = {
  totalCount: 0,
};

function HeroSection({ totalCount }) {
  const theme = useTheme();
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const handleScrollToSignup = () => {
    const el = document.getElementById('signup');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: '90vh' },
        display: 'flex',
        alignItems: 'center',
        py: { xs: 8, md: 4 },
      }}
    >
      <FloatingBlobs variant="hero" />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp(0)}>
              <Typography
                variant="overline"
                sx={{
                  color: orange,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  fontSize: '0.75rem',
                  mb: 1,
                  display: 'block',
                }}
              >
                For coaching institutes
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.1)}>
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.25rem', md: '3.5rem' },
                  lineHeight: 1.1,
                  color: 'text.primary',
                  mb: 2.5,
                  letterSpacing: '-0.02em',
                }}
              >
                Fill every batch.{' '}
                <RotatingWord orange={orange} golden={golden} />{' '}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  your institute.
                </Box>
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.2)}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.75,
                  mb: 4,
                  maxWidth: 480,
                }}
              >
                Get qualified student leads from aspirants actively studying across India.
                No ad spend. No cold calls. Just students who want what you teach.
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.3)}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleScrollToSignup}
                  sx={{
                    background: `linear-gradient(135deg, ${orange}, ${golden})`,
                    color: '#fff',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                      boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  List your institute
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: orange,
                    color: orange,
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: orange,
                      borderWidth: 2,
                      background: 'rgba(242,101,34,0.06)',
                    },
                  }}
                >
                  Chat on WhatsApp
                </Button>
              </Box>
            </motion.div>
          </Grid>

          {/* Right column — lead pipeline mock */}
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp(0.15)}>
              <LeadPipelineMock totalCount={totalCount} />
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

HeroSection.propTypes = {
  totalCount: PropTypes.number,
};

HeroSection.defaultProps = {
  totalCount: 0,
};

export default HeroSection;
